/**
 * Candy creature kit: the shared paint, pens, faces and glass bubble of the merge creatures
 * (chosen from the "candy" exploration: glossy candy toys in glass gem bubbles).
 *
 *   - Coloured line art instead of the one navy ink: every part is outlined in a deep shade of
 *     its own hue, thin in the light and swelling toward the lower right (candyPart's two offset
 *     ink fills).
 *   - Juicy rendering: four hard tones per part (bounce light hugging the shade-side edge,
 *     shade, base, light), a thin rim light on the lit edge and big wet speculars.
 *   - Huge sparkly eyes (candyFace / candyEye): a plum eye with a coloured iris ring, a big
 *     catchlight and a four-point sparkle; glossy cheeks; moods and custom rests ("wink",
 *     "proud"); "blink" closes only the eyes and keeps the resting mouth.
 *   - The disc is a see-through glass gem bubble (drawDiscCandy / discShineCandy; disc.js
 *     re-exports them): tinted glass deepening toward its coloured rim, a refraction caustic on
 *     the lower right, a window reflection on top; tone.band "gold" for the Dragon.
 *
 * Creature contract (creatures/index.js drawCreature): `disc` tones (base, light, shade, and
 * optionally ink = the rim colour, band, alpha), optional `discPattern(c, r)`, and
 * `draw(c, r, pose)` centred on the origin inside radius r. Deterministic, no shadowBlur/filter.
 */
import { mixHex, parseHex } from "../palette.js";
import { TAU, sparklePath } from "../toon-kit.js";

// ---------------------------------------------------------------------------------------
// Pen and paint (units of the disc radius r).

/**
 * Ink showing beyond a part's edge on its lit (upper-left) side; the shade side gets SWELL
 * more, so lines taper from thin in the light to heavy in the shade.
 */
export var PEN = { body: 0.03, part: 0.024, fine: 0.018 };
export var SWELL = [0.013, 0.019];
/** Face lines (closed eyes, mouths, brows). */
export var FACE_PEN = 0.03;
/** The plum every eye is painted in: darker than any hue so eyes lead at 30 px. */
export var EYE_INK = "#2a1238";
export var MOUTH_IN = "#8a1c46";
export var TONGUE = "#ff7a9c";

export function rgba(hex, a) {
  var p = parseHex(hex);
  return "rgba(" + p[0] + "," + p[1] + "," + p[2] + "," + a + ")";
}

/** A candy tone: ink (deep line), shade, base, light, bounce (reflected light on the shade edge). */
export function tone(ink, shade, base, light, bounce) {
  return { ink: ink, shade: shade, base: base, light: light, bounce: bounce };
}

export function fillShifted(c, path, color, dx, dy) {
  c.save();
  c.translate(dx, dy);
  c.beginPath();
  path(c);
  c.fillStyle = color;
  c.fill();
  c.restore();
}

/**
 * The coloured outline: the part's own shape stroked and filled in `ink` twice, the second copy
 * nudged toward the lower right, so the line is `pen` wide in the light and swells in the shade.
 */
export function candyInk(c, path, ink, pen, swell) {
  c.save();
  c.fillStyle = ink;
  c.strokeStyle = ink;
  c.lineJoin = "round";
  c.lineCap = "round";
  c.lineWidth = pen * 2;
  c.beginPath();
  path(c);
  c.fill();
  c.stroke();
  if (swell) {
    c.translate(swell[0], swell[1]);
    c.beginPath();
    path(c);
    c.fill();
    c.stroke();
  }
  c.restore();
}

/**
 * Paint one glossy candy part. `path(c)` adds the closed shape to the current path.
 * Layers: coloured tapered ink, bounce (fills the shape), shade and base (the shape shifted
 * up-left, leaving a bounce rim and a shade crescent on the lower right), light (the shape
 * shrunk toward the upper left, or `o.light`), a rim-light sliver on the lit edge, `o.inside`
 * (patterns, clipped), then the wet specular `o.spec` = [x, y, size, angle].
 *
 * @param {CanvasRenderingContext2D} c
 * @param {number} r disc radius (sets the pen)
 * @param {(c: CanvasRenderingContext2D) => void} path
 * @param {{ ink: string, shade: string, base: string, light: string, bounce: string }} t
 * @param {number} R the part's own radius (sets the shading offsets)
 * @param {Object} [o] { line, swell, cx, cy, lightK, light, rim, inside, spec, noOutline, bounceK }
 */
export function candyPart(c, r, path, t, R, o) {
  var opt = o || {};
  var pen = (opt.line != null ? opt.line : PEN.body) * r;
  var sw = opt.swell != null ? opt.swell : 1;
  if (!opt.noOutline) candyInk(c, path, t.ink, pen, [SWELL[0] * r * sw, SWELL[1] * r * sw]);
  c.save();
  c.beginPath();
  path(c);
  c.clip();
  var bk = opt.bounceK != null ? opt.bounceK : 1;
  fillShifted(c, path, t.bounce || t.shade, 0, 0);
  fillShifted(c, path, t.shade, -0.05 * R * bk, -0.065 * R * bk);
  fillShifted(c, path, t.base, -0.14 * R, -0.18 * R);
  if (opt.light !== false && t.light) {
    c.fillStyle = t.light;
    c.beginPath();
    if (typeof opt.light === "function") {
      opt.light(c);
    } else {
      var cx = opt.cx || 0, cy = opt.cy || 0, k = opt.lightK || 0.6;
      c.save();
      c.translate(cx - 0.2 * R, cy - 0.26 * R);
      c.scale(k, k);
      c.translate(-cx, -cy);
      path(c);
      c.restore();
    }
    c.fill();
  }
  if (opt.rim !== false) {
    // Rim light: the shape minus itself nudged down-right leaves a sliver on the lit edge.
    c.beginPath();
    path(c);
    c.save();
    c.translate(0.035 * R, 0.045 * R);
    path(c);
    c.restore();
    c.fillStyle = "rgba(255,255,255," + (opt.rim || 0.55) + ")";
    c.fill("evenodd");
  }
  if (opt.inside) opt.inside(c);
  c.restore();
  if (opt.spec) wetSpec(c, opt.spec[0], opt.spec[1], opt.spec[2], opt.spec[3]);
}

/**
 * A wet specular, gummy-candy style: a soft translucent pool, a crisp white bean in it and a
 * droplet beside it. (x, y) centre, s size, rot tilt (default leaning with the light).
 */
export function wetSpec(c, x, y, s, rot) {
  c.save();
  c.translate(x, y);
  c.rotate(rot != null ? rot : -0.65);
  c.fillStyle = "rgba(255,255,255,0.3)";
  c.beginPath();
  c.ellipse(s * 0.1, s * 0.05, s * 1.35, s * 0.82, 0, 0, TAU);
  c.fill();
  c.fillStyle = "rgba(255,255,255,0.97)";
  c.beginPath();
  c.ellipse(0, 0, s * 0.86, s * 0.42, 0, 0, TAU);
  c.fill();
  c.beginPath();
  c.arc(s * 1.12, s * 0.26, s * 0.19, 0, TAU);
  c.fill();
  c.restore();
}

/** A small crisp highlight dot (secondary specular on little parts). */
export function dotSpec(c, x, y, s, a) {
  c.fillStyle = "rgba(255,255,255," + (a || 0.95) + ")";
  c.beginPath();
  c.ellipse(x, y, s, s * 0.7, -0.6, 0, TAU);
  c.fill();
}

/** A rope (stalk, antenna) in candy ink: a deep stroke under a coloured core, both tapered by a nudge. */
export function candyRope(c, build, r, core, t) {
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  c.strokeStyle = t.ink;
  c.lineWidth = (core + PEN.part * 2) * r;
  c.beginPath();
  build(c);
  c.stroke();
  c.save();
  c.translate(SWELL[0] * r * 0.6, SWELL[1] * r * 0.6);
  c.beginPath();
  build(c);
  c.stroke();
  c.restore();
  c.strokeStyle = t.base;
  c.lineWidth = core * r;
  c.beginPath();
  build(c);
  c.stroke();
  c.strokeStyle = rgba(t.light, 0.9);
  c.lineWidth = core * r * 0.35;
  c.save();
  c.translate(-core * r * 0.18, -core * r * 0.18);
  c.beginPath();
  build(c);
  c.stroke();
  c.restore();
  c.restore();
}

// ---------------------------------------------------------------------------------------
// Path helpers (all in disc-radius units unless noted).

/** A closed Catmull-Rom curve through unit points, scaled by r. */
export function smoothPath(c, pts, r) {
  var n = pts.length;
  c.moveTo(pts[0][0] * r, pts[0][1] * r);
  for (var i = 0; i < n; i++) {
    var p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
    c.bezierCurveTo(
      (p1[0] + (p2[0] - p0[0]) / 6) * r, (p1[1] + (p2[1] - p0[1]) / 6) * r,
      (p2[0] - (p3[0] - p1[0]) / 6) * r, (p2[1] - (p3[1] - p1[1]) / 6) * r,
      p2[0] * r, p2[1] * r
    );
  }
  c.closePath();
}

/** Points mirrored (x → −x) when side < 0, rotated by `rot` about pivot (px, py). */
export function placePts(pts, side, rot, px, py, sx) {
  var out = [];
  var ca = Math.cos(rot || 0), sa = Math.sin(rot || 0);
  var k = sx != null ? sx : 1;
  for (var i = 0; i < pts.length; i++) {
    var x = (pts[i][0] - px) * k, y = pts[i][1] - py;
    var rx = x * ca - y * sa + px, ry = x * sa + y * ca + py;
    out.push([side * rx, ry]);
  }
  return out;
}

export function ellipsePath(x, y, rx, ry, rot) {
  return function (c) {
    c.moveTo(x + Math.cos(rot || 0) * rx, y + Math.sin(rot || 0) * rx);
    c.ellipse(x, y, rx, ry, rot || 0, 0, TAU);
  };
}

/** A heart centred on (x, y), s ≈ half its width, point down (rot turns it). */
export function heartPath(c, x, y, s, rot) {
  c.save();
  c.translate(x, y);
  c.rotate(rot || 0);
  c.moveTo(0, s * 0.95);
  c.bezierCurveTo(-s * 0.55, s * 0.55, -s * 1.15, s * 0.1, -s * 0.95, -s * 0.42);
  c.bezierCurveTo(-s * 0.78, -s * 0.92, -s * 0.18, -s * 0.95, 0, -s * 0.45);
  c.bezierCurveTo(s * 0.18, -s * 0.95, s * 0.78, -s * 0.92, s * 0.95, -s * 0.42);
  c.bezierCurveTo(s * 1.15, s * 0.1, s * 0.55, s * 0.55, 0, s * 0.95);
  c.closePath();
  c.restore();
}

/** A flame tongue: round foot of radius rr at (x, y), licking up h, tip swayed by sw. */
export function candyFlamePath(c, x, y, rr, h, sw) {
  c.moveTo(x - rr, y);
  c.bezierCurveTo(x - rr * 1.05, y - h * 0.45, x + sw - rr * 0.45, y - h * 0.62, x + sw, y - h);
  c.bezierCurveTo(x + sw + rr * 0.3, y - h * 0.68, x + rr * 1.05, y - h * 0.45, x + rr, y);
  c.arc(x, y, rr, 0, Math.PI);
  c.closePath();
}

/** Samples of a cubic bezier (unit points), n + 1 of them. */
export function bezierPts(p0, p1, p2, p3, n) {
  var out = [];
  for (var i = 0; i <= n; i++) {
    var t = i / n, u = 1 - t;
    out.push([
      u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
      u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
    ]);
  }
  return out;
}

/** A closed tube round a centreline (unit points), half-width w0 → w1, with a round tip. */
export function tubePath(pts, w0, w1, r) {
  return function (c) {
    var n = pts.length, L = [], Rr = [];
    for (var i = 0; i < n; i++) {
      var a = pts[Math.max(0, i - 1)], b = pts[Math.min(n - 1, i + 1)];
      var dx = b[0] - a[0], dy = b[1] - a[1], d = Math.hypot(dx, dy) || 1;
      var nx = -dy / d, ny = dx / d;
      var w = w0 + (w1 - w0) * (i / (n - 1));
      L.push([(pts[i][0] + nx * w) * r, (pts[i][1] + ny * w) * r]);
      Rr.push([(pts[i][0] - nx * w) * r, (pts[i][1] - ny * w) * r]);
    }
    c.moveTo(L[0][0], L[0][1]);
    for (i = 1; i < n; i++) c.lineTo(L[i][0], L[i][1]);
    var e = pts[n - 1], f = pts[n - 2];
    var ang = Math.atan2(e[1] - f[1], e[0] - f[0]);
    c.arc(e[0] * r, e[1] * r, w1 * r, ang + Math.PI / 2, ang - Math.PI / 2, true);
    for (i = n - 1; i >= 0; i--) c.lineTo(Rr[i][0], Rr[i][1]);
    c.closePath();
  };
}

// ---------------------------------------------------------------------------------------
// The candy face.

/**
 * Moods → [eyes, mouth]. The shared cuteFace moods plus each creature's signature rest:
 * "wink" (Fish: one eye closed, lips puckered round a bubble) and "proud" (Dragon: chin-up
 * brows, a smug fanged smirk).
 */
export var MOODS = {
  smile: ["open", "smile"],
  grin: ["open", "grin"],
  happy: ["happy", "grin"],
  wow: ["wow", "wow"],
  blink: ["blink", null],
  sleepy: ["sleepy", "sleepy"],
  fierce: ["fierce", "fierce"],
  cool: ["cool", "smirk"],
  wink: ["open", "pucker"],
  proud: ["proud", "smirk"],
};

/**
 * Paint a candy face centred between the eyes at (x, y). spec (r units): eye (radius),
 * gap (eye centre to centre), mouth (half-width), drop (mouth below the eye line, in eye
 * radii), mouthAt ([x, y] absolute override for the mouth), ink (the skin's deep line colour),
 * iris ({ base, light }), blush, cheek ([spread × gap, drop in eye radii]), lashes, clip (path
 * of the head; cheeks and mouth stay inside it), look ([dx, dy] in eye radii), wink (side whose
 * eye closes at rest: −1 left, 1 right), fang (side of a little fang at the smirk / grin).
 * Returns { mouthMood, mx, my, mw, eyeR }.
 */
export function candyFace(c, r, x, y, spec, pose, rest) {
  var sp = spec || {};
  var mood = (pose && pose.mood) || rest;
  var m = MOODS[mood] || MOODS.smile;
  var eyeKind = m[0];
  var mouthMood = m[1] || MOODS[rest][1];
  var e = (sp.eye != null ? sp.eye : 0.13) * r;
  var gap = (sp.gap != null ? sp.gap : 0.38) * r;
  var mw = (sp.mouth != null ? sp.mouth : 0.085) * r;
  var drop = sp.drop != null ? sp.drop : 1.6;
  var mx = sp.mouthAt ? sp.mouthAt[0] * r : x;
  var my = sp.mouthAt && mouthMood === MOODS[rest][1] ? sp.mouthAt[1] * r : y + e * drop;
  if (sp.mouthAt && mouthMood !== MOODS[rest][1]) mx = x;

  c.save();
  if (sp.clip) {
    c.beginPath();
    sp.clip(c);
    c.clip();
  }
  // Glossy cheeks: a soft pink pool and a crisp dot of shine.
  var ck = sp.cheek || [0.98, 1.25];
  for (var b = -1; b <= 1; b += 2) {
    var bx = x + b * gap * ck[0], by = y + e * ck[1];
    c.fillStyle = rgba(sp.blush || "#ff5aa8", sp.blushAlpha || 0.66);
    c.beginPath();
    c.ellipse(bx, by, e * 0.95, e * 0.58, 0, 0, TAU);
    c.fill();
    c.fillStyle = "rgba(255,255,255,0.85)";
    c.beginPath();
    c.ellipse(bx - e * 0.32, by - e * 0.16, e * 0.17, e * 0.11, -0.4, 0, TAU);
    c.fill();
  }
  candyMouth(c, r, mx, my, mw, mouthMood, sp);
  c.restore();

  for (var side = -1; side <= 1; side += 2) {
    var kind = eyeKind;
    if (sp.wink === side && mood === rest) kind = "wink";
    var lx = sp.look ? sp.look[0] * e : 0, ly = sp.look ? sp.look[1] * e : 0;
    candyEye(c, r, x + side * gap * 0.5 + lx, y + ly, e, kind, side, sp);
  }
  return { mouthMood: mouthMood, mx: mx, my: my, mw: mw, eyeR: e };
}

/** One eye of a given kind; side −1 left, 1 right (lashes and brows flip). */
export function candyEye(c, r, x, y, e, kind, side, sp) {
  var lw = FACE_PEN * r;
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = EYE_INK;
  c.fillStyle = EYE_INK;
  c.lineWidth = lw;
  if (kind === "blink" || kind === "sleepy") {
    // A closed lid curving down like a smile, with a lash flick at the outer corner.
    var yy = y + e * (kind === "sleepy" ? 0.45 : 0.2);
    var sag = kind === "sleepy" ? 0.45 : 0.62;
    c.beginPath();
    c.moveTo(x - e * 0.92, yy - e * 0.12);
    c.quadraticCurveTo(x, yy + e * sag * 1.4, x + e * 0.92, yy - e * 0.12);
    c.stroke();
    if (sp.lashes !== false) {
      c.beginPath();
      c.moveTo(x + side * e * 0.88, yy - e * 0.1);
      c.lineTo(x + side * e * 1.22, yy - e * 0.42);
      c.stroke();
    }
  } else if (kind === "happy" || kind === "wink") {
    // A springy ^ arch (a wink keeps it a little tighter, like a squeezed eye).
    c.lineWidth = lw * 1.15;
    c.beginPath();
    if (kind === "wink") {
      c.moveTo(x - e * 0.85, y + e * 0.05);
      c.quadraticCurveTo(x - e * 0.1, y - e * 0.95, x + e * 0.85, y + e * 0.25);
    } else {
      c.moveTo(x - e * 0.9, y + e * 0.35);
      c.quadraticCurveTo(x, y - e * 0.95, x + e * 0.9, y + e * 0.35);
    }
    c.stroke();
    if (kind === "wink" && sp.lashes !== false) {
      c.beginPath();
      c.moveTo(x + side * e * 0.82, y + e * 0.08);
      c.lineTo(x + side * e * 1.18, y - e * 0.14);
      c.stroke();
    }
  } else {
    var big = kind === "wow" ? 1.14 : 1;
    sparkleEye(c, x, y, e * big, sp.iris || IRIS.teal, kind === "wow");
    if (sp.lashes) {
      // Two lash flicks on the outer top of the eye.
      c.lineWidth = lw * 0.9;
      c.beginPath();
      var ex = x + side * e * 0.62, ey = y - e * 1.0;
      c.moveTo(ex, ey);
      c.lineTo(ex + side * e * 0.42, ey - e * 0.28);
      c.moveTo(x + side * e * 0.9, y - e * 0.62);
      c.lineTo(x + side * e * 1.3, y - e * 0.74);
      c.stroke();
    }
    if (kind === "cool") {
      // A heavy lid across the top third: smug.
      c.save();
      c.beginPath();
      c.rect(x - e * 1.4, y - e * 1.6, e * 2.8, e * 1.05);
      c.clip();
      c.fillStyle = sp.lid || "#ffffff";
      c.beginPath();
      c.ellipse(x, y, e * 1.06, e * 1.28, 0, 0, TAU);
      c.fill();
      c.restore();
      c.lineWidth = lw * 1.1;
      c.beginPath();
      c.moveTo(x - e * 1.05, y - e * 0.52);
      c.lineTo(x + e * 1.05, y - e * 0.58);
      c.stroke();
    }
    if (kind === "fierce" || kind === "proud") {
      // A brow: fierce dips toward the nose, proud lifts confidently at the inner end.
      c.lineWidth = lw * 1.2;
      c.strokeStyle = sp.ink || EYE_INK;
      c.beginPath();
      if (kind === "fierce") {
        c.moveTo(x - side * e * 0.95, y - e * 1.25);
        c.lineTo(x + side * e * 0.85, y - e * 1.75);
      } else {
        c.moveTo(x - side * e * 0.75, y - e * 1.72);
        c.quadraticCurveTo(x + side * e * 0.1, y - e * 1.95, x + side * e * 0.9, y - e * 1.55);
      }
      c.stroke();
    }
  }
  c.restore();
}

/** Iris colours (ring + the light pooling at the bottom of the eye). */
export var IRIS = {
  teal: { base: "#2fd2c4", light: "#b4fff2" },
  lime: { base: "#5fe07a", light: "#d8ffb0" },
  blue: { base: "#4aa8ff", light: "#c4e6ff" },
  pink: { base: "#ff5fb4", light: "#ffd0ec" },
  violet: { base: "#a07cff", light: "#e6dcff" },
  gold: { base: "#ffb52e", light: "#fff0a8" },
};

/**
 * A big sparkly eye: plum oval, a coloured iris ring and a glow pooling at the bottom, a
 * dark pupil, a big tilted catchlight upper left, a four-point sparkle lower right.
 */
export function sparkleEye(c, x, y, e, iris, wow) {
  var ry = e * 1.2;
  c.save();
  c.fillStyle = EYE_INK;
  c.beginPath();
  c.ellipse(x, y, e, ry, 0, 0, TAU);
  c.fill();
  c.clip();
  c.fillStyle = iris.base;
  c.beginPath();
  c.ellipse(x, y + ry * 0.12, e * 0.8, ry * 0.8, 0, 0, TAU);
  c.fill();
  c.fillStyle = iris.light;
  c.beginPath();
  c.ellipse(x, y + ry * 0.66, e * 0.62, ry * 0.36, 0, 0, TAU);
  c.fill();
  c.fillStyle = EYE_INK;
  c.beginPath();
  c.ellipse(x, y - ry * 0.04, e * (wow ? 0.42 : 0.58), ry * (wow ? 0.46 : 0.6), 0, 0, TAU);
  c.fill();
  c.restore();
  c.fillStyle = "#ffffff";
  c.beginPath();
  c.ellipse(x - e * 0.3, y - ry * 0.38, e * 0.44, e * 0.34, -0.5, 0, TAU);
  c.fill();
  c.beginPath();
  sparklePath(c, x + e * 0.36, y + ry * 0.3, e * 0.36, 0);
  c.fill();
  c.beginPath();
  c.arc(x - e * 0.5, y + ry * 0.36, e * 0.1, 0, TAU);
  c.fill();
}

/** The mouth for a mood, centred at (x, y) with half-width w. */
export function candyMouth(c, r, x, y, w, kind, sp) {
  var ink = sp.ink || EYE_INK;
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = ink;
  c.lineWidth = FACE_PEN * r;
  var fang = sp.fang || 0;
  if (kind === "smile") {
    c.beginPath();
    c.moveTo(x - w, y - w * 0.15);
    c.quadraticCurveTo(x, y + w * 1.05, x + w, y - w * 0.15);
    c.stroke();
  } else if (kind === "grin" || kind === "fierce") {
    // An open D-mouth: dark inside, a tongue, top teeth for fierce.
    var shape = function (cc) {
      cc.moveTo(x - w, y - w * 0.2);
      cc.quadraticCurveTo(x, y + w * 0.05, x + w, y - w * 0.2);
      cc.bezierCurveTo(x + w * 0.95, y + w * 1.15, x - w * 0.95, y + w * 1.15, x - w, y - w * 0.2);
      cc.closePath();
    };
    c.beginPath();
    shape(c);
    c.fillStyle = MOUTH_IN;
    c.fill();
    c.save();
    c.clip();
    c.fillStyle = TONGUE;
    c.beginPath();
    c.ellipse(x + w * 0.08, y + w * 0.95, w * 0.62, w * 0.48, 0, 0, TAU);
    c.fill();
    if (kind === "fierce") {
      c.fillStyle = "#ffffff";
      c.fillRect(x - w, y - w * 0.4, w * 2, w * 0.42);
    }
    c.restore();
    c.beginPath();
    shape(c);
    c.stroke();
    if (fang) toothAt(c, r, x + fang * w * 0.5, y - w * 0.05, w * 0.28);
  } else if (kind === "wow") {
    c.beginPath();
    c.ellipse(x, y + w * 0.35, w * 0.5, w * 0.62, 0, 0, TAU);
    c.fillStyle = MOUTH_IN;
    c.fill();
    c.save();
    c.clip();
    c.fillStyle = TONGUE;
    c.beginPath();
    c.ellipse(x, y + w * 0.9, w * 0.42, w * 0.3, 0, 0, TAU);
    c.fill();
    c.restore();
    c.stroke();
  } else if (kind === "sleepy") {
    c.beginPath();
    c.ellipse(x, y + w * 0.3, w * 0.28, w * 0.34, 0, 0, TAU);
    c.fillStyle = MOUTH_IN;
    c.fill();
    c.stroke();
  } else if (kind === "smirk") {
    c.beginPath();
    c.moveTo(x - w * 0.95, y + w * 0.05);
    c.quadraticCurveTo(x - w * 0.05, y + w * 0.75, x + w * 0.95, y - w * 0.32);
    c.stroke();
    if (fang) toothAt(c, r, x + fang * w * 0.42, y + w * 0.3, w * 0.3);
  } else if (kind === "pucker") {
    // Round kissy lips with a dark centre.
    c.beginPath();
    c.ellipse(x, y, w * 0.55, w * 0.6, 0, 0, TAU);
    c.fillStyle = sp.lips || "#ff6f9a";
    c.fill();
    c.stroke();
    c.beginPath();
    c.ellipse(x - w * 0.02, y + w * 0.04, w * 0.22, w * 0.26, 0, 0, TAU);
    c.fillStyle = MOUTH_IN;
    c.fill();
    dotSpec(c, x - w * 0.24, y - w * 0.28, w * 0.13);
  }
  c.restore();
}

/** A little white fang hanging from (x, y). */
export function toothAt(c, r, x, y, s) {
  c.save();
  c.beginPath();
  c.moveTo(x - s * 0.55, y - s * 0.1);
  c.lineTo(x + s * 0.55, y - s * 0.1);
  c.lineTo(x + s * 0.05, y + s * 0.95);
  c.closePath();
  c.fillStyle = "#ffffff";
  c.fill();
  c.lineWidth = FACE_PEN * r * 0.6;
  c.stroke();
  c.restore();
}

// ---------------------------------------------------------------------------------------
// The glass gem bubble (drop-in for creatures/disc.js).

/**
 * The creature's bubble: tinted see-through glass, deeper and more saturated toward its edge
 * like real glass, a ring of light just inside the rim (the glass's thickness), a refraction
 * caustic pooling on the lower right, the tier's pattern, and a coloured rim (`tone.ink`, or a
 * deep shade of `tone.shade`). `tone.band: "gold"` swaps the rim for a studded gold band.
 *
 * @param {CanvasRenderingContext2D} c
 * @param {number} r
 * @param {{ base: string, light: string, shade: string, ink?: string, band?: string, alpha?: number }} t
 * @param {{ pattern?: (c: CanvasRenderingContext2D, r: number) => void }} [opts]
 */
export function drawDiscCandy(c, r, t, opts) {
  var o = opts || {};
  var deep = t.ink || mixHex(t.shade, "#1a0a30", 0.35);
  var a = t.alpha != null ? t.alpha : 1;
  c.save();
  c.beginPath();
  c.arc(0, 0, r, 0, TAU);
  c.clip();
  // Glass body: clear-ish tint in the middle, saturated at the edge (a Fresnel look).
  var g = c.createRadialGradient(-r * 0.22, -r * 0.28, r * 0.05, 0, 0, r);
  g.addColorStop(0, rgba(mixHex(t.light, t.base, 0.45), 0.7 * a));
  g.addColorStop(0.5, rgba(t.base, 0.74 * a));
  g.addColorStop(0.82, rgba(t.base, 0.86 * a));
  g.addColorStop(1, rgba(t.shade, 0.98));
  c.fillStyle = g;
  c.fillRect(-r, -r, r * 2, r * 2);
  // Inner band: a deeper ring hugging the rim on the shade side, the glass's body colour.
  c.beginPath();
  c.arc(0, 0, r, 0, TAU);
  c.arc(-r * 0.06, -r * 0.08, r * 0.9, 0, TAU, true);
  c.fillStyle = rgba(t.shade, 0.55);
  c.fill();
  // Refraction caustic: light focused through the sphere pools on the lower right.
  c.beginPath();
  c.arc(0, 0, r * 0.88, 0, TAU);
  c.arc(-r * 0.1, -r * 0.13, r * 0.86, 0, TAU, true);
  c.fillStyle = rgba(t.light, 0.75);
  c.fill("evenodd");
  if (o.pattern) o.pattern(c, r);
  // The glass's thickness: a fine light ring just inside the rim.
  c.beginPath();
  c.arc(0, 0, r * 0.9, 0, TAU);
  c.strokeStyle = "rgba(255,255,255,0.32)";
  c.lineWidth = r * 0.022;
  c.stroke();
  c.restore();
  // Rim.
  if (t.band === "gold") {
    goldBand(c, r);
  } else {
    // A fine rim: half the old weight, its outer edge where it was.
    c.beginPath();
    c.arc(0, 0, r * 0.99875, 0, TAU);
    c.strokeStyle = deep;
    c.lineWidth = r * 0.0275;
    c.stroke();
    c.beginPath();
    c.arc(0, 0, r * 0.945, Math.PI * 0.95, Math.PI * 1.6);
    c.strokeStyle = "rgba(255,255,255,0.55)";
    c.lineWidth = r * 0.018;
    c.lineCap = "round";
    c.stroke();
  }
}

/** The Dragon's crown band: a gold ring with a deep amber edge and round gem studs. */
export function goldBand(c, r) {
  var GOLD = { ink: "#9a4f08", shade: "#e6901c", base: "#ffc83a", light: "#fff3a8" };
  c.save();
  c.lineCap = "round";
  c.beginPath();
  c.arc(0, 0, r * 0.955, 0, TAU);
  c.strokeStyle = GOLD.ink;
  c.lineWidth = r * 0.13;
  c.stroke();
  c.strokeStyle = GOLD.shade;
  c.lineWidth = r * 0.085;
  c.stroke();
  c.beginPath();
  c.arc(-r * 0.012, -r * 0.016, r * 0.955, 0, TAU);
  c.strokeStyle = GOLD.base;
  c.lineWidth = r * 0.06;
  c.stroke();
  c.beginPath();
  c.arc(0, 0, r * 0.955, Math.PI * 0.9, Math.PI * 1.65);
  c.strokeStyle = GOLD.light;
  c.lineWidth = r * 0.025;
  c.stroke();
  var gems = ["#ff4f7a", "#4fc8ff", "#7cf06a", "#ff4f7a", "#b48cff", "#4fc8ff", "#ffe066", "#b48cff"];
  for (var i = 0; i < 8; i++) {
    var ang = -Math.PI / 2 + (i / 8) * TAU;
    var x = Math.cos(ang) * r * 0.955, y = Math.sin(ang) * r * 0.955;
    c.beginPath();
    c.arc(x, y, r * 0.046, 0, TAU);
    c.fillStyle = GOLD.ink;
    c.fill();
    c.beginPath();
    c.arc(x, y, r * 0.032, 0, TAU);
    c.fillStyle = gems[i];
    c.fill();
    dotSpec(c, x - r * 0.01, y - r * 0.012, r * 0.011);
  }
  c.restore();
}

/**
 * The bubble's reflection, painted over the creature (and kept upright in game): a curved
 * window highlight along the upper-left rim, a droplet under it and a small glint low right.
 */
export function discShineCandy(c, r) {
  c.save();
  // Curved window: a crescent between two offset arcs, fat in the middle, tapering out.
  c.beginPath();
  c.arc(0, 0, r * 0.86, Math.PI * 1.1, Math.PI * 1.45);
  c.arc(r * 0.05, r * 0.06, r * 0.82, Math.PI * 1.43, Math.PI * 1.12, true);
  c.closePath();
  c.fillStyle = "rgba(255,255,255,0.88)";
  c.fill();
  c.beginPath();
  c.arc(-r * 0.74, -r * 0.18, r * 0.045, 0, TAU);
  c.fill();
  c.fillStyle = "rgba(255,255,255,0.55)";
  c.beginPath();
  c.arc(0, 0, r * 0.86, Math.PI * 0.18, Math.PI * 0.3);
  c.arc(-r * 0.03, -r * 0.03, r * 0.84, Math.PI * 0.3, Math.PI * 0.18, true);
  c.closePath();
  c.fill();
  c.restore();
}

/**
 * A glass bubble at (x, y), radius s: a tint, a caustic pooling low right, a light rim and a
 * spec. With `ink` it also gets a coloured outline, so it reads as an object (the Fish's
 * breath) rather than a pattern in the glass.
 */
export function glassBubble(c, x, y, s, rim, tint, alpha, ink) {
  c.save();
  c.globalAlpha = alpha != null ? alpha : 1;
  if (ink) {
    c.beginPath();
    c.arc(x + s * 0.04, y + s * 0.06, s * 1.16, 0, TAU);
    c.fillStyle = ink;
    c.fill();
  }
  c.beginPath();
  c.arc(x, y, s, 0, TAU);
  c.fillStyle = rgba(tint, ink ? 0.62 : 0.35);
  c.fill();
  c.beginPath();
  c.arc(x, y, s * 0.95, 0, TAU);
  c.arc(x - s * 0.14, y - s * 0.18, s * 0.86, 0, TAU, true);
  c.fillStyle = "rgba(255,255,255,0.7)";
  c.fill("evenodd");
  c.beginPath();
  c.arc(x, y, s * (ink ? 0.9 : 1), 0, TAU);
  c.strokeStyle = rim;
  c.lineWidth = Math.max(0.6, s * (ink ? 0.12 : 0.16));
  c.stroke();
  c.fillStyle = "#ffffff";
  c.beginPath();
  c.ellipse(x - s * 0.36, y - s * 0.38, s * 0.32, s * 0.2, -0.7, 0, TAU);
  c.fill();
  c.beginPath();
  c.arc(x + s * 0.02, y - s * 0.6, s * 0.08, 0, TAU);
  c.fill();
  c.restore();
}

/** Four-point sparkles [x, y, size, rot] (r units) in white over a coloured edge. */
export function sparkles(c, r, list, edge, fill) {
  c.save();
  for (var i = 0; i < list.length; i++) {
    var s = list[i];
    c.beginPath();
    sparklePath(c, s[0] * r, s[1] * r, s[2] * r * 1.35, s[3] || 0);
    c.fillStyle = edge;
    c.fill();
    c.beginPath();
    sparklePath(c, s[0] * r, s[1] * r, s[2] * r, s[3] || 0);
    c.fillStyle = fill || "#ffffff";
    c.fill();
  }
  c.restore();
}
